import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bell, CheckCheck, Inbox, Circle } from "lucide-react";
import { toast } from "sonner";

const SEV_META = {
  info: { color: "text-neutral-500 bg-neutral-100", label: "Info" },
  success: { color: "text-teal bg-teal/10", label: "Good news" },
  warning: { color: "text-pink bg-pink/10", label: "Heads up" },
  danger: { color: "text-red-500 bg-red-500/10", label: "Urgent" },
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); return `${d}d ago`;
};

export default function NotificationsPage() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications || []);
    } catch {
      toast.error("Couldn't load notifications");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = async (n) => {
    if (n.read_at) return;
    if (!n.ephemeral) {
      try { await api.patch(`/notifications/${n.notif_id}/read`); } catch {}
    }
    setItems(prev => prev.map(x => x.notif_id === n.notif_id ? { ...x, read_at: new Date().toISOString() } : x));
  };

  const markAll = async () => {
    try { await api.patch("/notifications/read-all"); toast.success("Marked all as read"); } catch {}
    setItems(prev => prev.map(x => ({ ...x, read_at: x.read_at || new Date().toISOString() })));
  };

  const openItem = (n) => {
    markRead(n);
    if (n.cta_link) {
      const [pathPart, hashPart] = n.cta_link.split("#");
      const sep = pathPart.includes("?") ? "&" : "?";
      const query = `${pathPart}${sep}notif=${encodeURIComponent(n.notif_id)}&notif_title=${encodeURIComponent(n.title)}`;
      nav(hashPart ? `${query}#${hashPart}` : query);
    }
  };

  const filtered = items.filter(n => filter === "all" ? true : (filter === "unread" ? !n.read_at : n.type === filter));
  const unreadCount = items.filter(n => !n.read_at).length;

  // Group by day
  const groups = filtered.reduce((acc, n) => {
    const day = new Date(n.created_at).toDateString();
    (acc[day] ||= []).push(n);
    return acc;
  }, {});
  const groupKeys = Object.keys(groups);

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="notifications-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold font-display flex items-center gap-2">
            <Inbox className="w-6 h-6 text-pink" /> Notifications
          </h1>
          <p className="text-sm text-neutral-500 mt-1">
            {unreadCount > 0 ? `${unreadCount} unread · ${items.length} total` : `All caught up · ${items.length} total`}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={markAll}
          disabled={unreadCount === 0}
          data-testid="notif-page-mark-all"
          className="rounded-full">
          <CheckCheck className="w-4 h-4 mr-1.5" /> Mark all read
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="notif-filters">
        {[
          { key: "all", label: "All", count: items.length },
          { key: "unread", label: "Unread", count: unreadCount },
          { key: "NEW_SUBMISSION", label: "Submissions" },
          { key: "CANDIDATE_WITHDREW", label: "Withdrawals" },
          { key: "CANDIDATE_HIRED", label: "Hires" },
          { key: "PACKET_COMMENT", label: "Committee" },
          { key: "COMPLIANCE_FAILING", label: "Compliance" },
          { key: "CERT_EXPIRING_SOON", label: "Certs" },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`notif-filter-${f.key.toLowerCase()}`}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              filter === f.key
                ? "bg-navy text-white border-navy"
                : "bg-white text-neutral-600 border-neutral-200 hover:border-pink hover:text-pink"
            }`}>
            {f.label}{f.count != null ? ` · ${f.count}` : ""}
          </button>
        ))}
      </div>

      {loading && (
        <div className="text-center text-sm text-neutral-500 py-10">Loading…</div>
      )}

      {!loading && filtered.length === 0 && (
        <Card className="border-0 shadow-sm" data-testid="notif-page-empty">
          <CardContent className="p-16 text-center">
            <Bell className="w-10 h-10 text-neutral-300 mx-auto" />
            <div className="text-neutral-600 font-semibold mt-3">Nothing here.</div>
            <div className="text-sm text-neutral-500 mt-1">
              {filter === "unread" ? "Every notification has been read." : "You'll see updates from Alfred and your teammates as they happen."}
            </div>
          </CardContent>
        </Card>
      )}

      {!loading && groupKeys.map((day) => (
        <div key={day} data-testid={`notif-day-group`}>
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold mb-2 px-1">
            {new Date(day).toDateString() === new Date().toDateString()
              ? "Today"
              : new Date(Date.now() - 86400000).toDateString() === day
                ? "Yesterday"
                : new Date(day).toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" })}
          </div>
          <Card className="border-0 shadow-sm overflow-hidden">
            <CardContent className="p-0">
              {groups[day].map((n, i) => {
                const sev = SEV_META[n.severity || "info"];
                const isUnread = !n.read_at;
                return (
                  <button
                    key={n.notif_id}
                    onClick={() => openItem(n)}
                    className={`w-full text-left px-5 py-4 border-b last:border-b-0 hover:bg-neutral-50 transition-colors flex gap-4 ${isUnread ? "bg-pink/[0.02]" : ""}`}
                    data-testid={`notif-row-${n.notif_id}`}>
                    <div className={`w-10 h-10 rounded-full grid place-items-center shrink-0 ${sev.color}`}>
                      <Bell className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2 min-w-0">
                          {isUnread && <Circle className="w-2 h-2 fill-pink text-pink shrink-0" />}
                          <div className={`text-sm ${isUnread ? "font-semibold text-neutral-900" : "text-neutral-700"} truncate`}>{n.title}</div>
                          {n.ephemeral && <Badge variant="outline" className="text-[9px] h-4 shrink-0">Live</Badge>}
                        </div>
                        <div className="text-[10px] text-neutral-400 shrink-0">{timeAgo(n.created_at)}</div>
                      </div>
                      <div className="text-sm text-neutral-600 mt-1">{n.body}</div>
                      {n.cta_link && (
                        <div className="text-xs text-pink font-semibold mt-1.5">
                          Open →
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </CardContent>
          </Card>
        </div>
      ))}
    </div>
  );
}
